import { Button, GridContainer, GridItem, Heading } from "@ncs/web-legos/components"
import { AddedToCartModalEcomm, PageContentWrapper } from "~/shared-components"
import {
	actionsContainerStyle,
	ctaAddAllStyle,
	ctaClearFavoritesStyle,
	favoriteHeadingStyle,
	headerStyle,
	itemsQuantityStyle,
} from "./styles"
import { useFavorites } from "~/hooks/useFavorites"
import {
	isNonParentOnlinePart,
	OnlinePartEcomm,
	useOnliePartsEcommService,
} from "@ncs/ncs-api/portal-apps"
import {
	mapNonParentOnlinePartToNonParentCustomerPart,
	PartCard,
} from "../part-search-results/components"
import { Box } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { trackEvent, TrackingEvent } from "@ncs/web-legos/util"
import { useState } from "react"
import { useShopContext } from "~/contexts"

export const Favorites: React.FC = () => {
	const { favoriteIds, handleClearFavorites, isFetchingFavorites } = useFavorites()
	const [addedPart, setAddedPart] = useState<OnlinePartEcomm | null>(null)
	const [addedPartIds, setAddedPartIds] = useState<string[]>([])
	const [, shopDispatch] = useShopContext()

	const [customerParts, isLoadingCustomerParts] = useOnliePartsEcommService({
		params: {
			onlinePartIds: favoriteIds,
		},
		queryConfig: {
			enabled: favoriteIds.length > 0,
		},
	})

	const handleAddToCart = (selectedPart: OnlinePartEcomm) => {
		trackEvent(TrackingEvent.FAVORITE_ADD_TO_CART, {
			id: selectedPart.id,
			item_value: selectedPart.price,
			quantity: 1,
			name: selectedPart.part?.description,
			part_title_name: selectedPart.title,
			part_number: selectedPart.onlinePartNumber,
		})
		setAddedPart(selectedPart)
	}

	const handleAddAllToCart = () => {
		// Only non-parent parts can be added directly to the cart;
		const partsToAdd = (customerParts ?? []).filter(isNonParentOnlinePart)
		if (partsToAdd.length === 0) return

		const products = partsToAdd.map((part) => ({
			part: mapNonParentOnlinePartToNonParentCustomerPart(part),
			quantity: 1,
		}))

		shopDispatch({
			type: "add parts to cart",
			payload: products,
		})

		products.forEach(({ part, quantity }) => {
			trackEvent(TrackingEvent.FAVORITE_ADD_TO_CART, {
				id: part.id,
				item_value: part.price,
				quantity,
				name: part.part?.description,
				part_title_name: part.title,
				part_number: part.onlinePartNumber,
			})
		})

		setAddedPartIds(products.map(({ part }) => part.id))
	}

	return (
		<PageContentWrapper
			title="Favorites"
			breadcrumbs={[{ name: "Shop", to: "/shop" }, { name: "Favorites" }]}
		>
			<section css={headerStyle}>
				<Heading variant="h2" css={favoriteHeadingStyle}>
					Favorites
				</Heading>
				<p css={itemsQuantityStyle}>{favoriteIds.length} Items</p>
			</section>
			<GridContainer
				totalColumnCount={20}
				rowGap="32px"
				columnGap="24px"
				m="0 auto"
				maxWidth="1260px"
			>
				{isLoadingCustomerParts || isFetchingFavorites ?
					Array.from({ length: 5 }).map((_, index) => (
						<GridItem key={index} xs={20} sm={10} md={5} lg={4}>
							<Box display="flex" flexDirection="column" key={index}>
								<Skeleton variant="rect" height={200} width={220} />
								<Box mt={3}>
									<Skeleton variant="text" height={44} width={220} />
								</Box>
								<Box>
									<Skeleton variant="text" height={22} width={120} />
								</Box>
								<Box mt={3}>
									<Skeleton variant="text" height={42} width={120} />
								</Box>
								<Box mt={3}>
									<Skeleton variant="text" height={"52px"} width={220} />
								</Box>
							</Box>
						</GridItem>
					))
				:	customerParts?.map((part: any) => (
						<GridItem key={part.id} xs={20} sm={10} md={5} lg={4}>
							<PartCard
								part={part as any}
								onAddToCart={() => handleAddToCart(part)}
								favoritesEnabled
								widthMode="fluid"
							/>
						</GridItem>
					))
				}
			</GridContainer>

			<section css={actionsContainerStyle}>
				<Button
					variant="clear"
					css={ctaClearFavoritesStyle}
					onClick={handleClearFavorites}
				>
					Clear Favorites
				</Button>
				<Button
					variant="primary-cta"
					css={ctaAddAllStyle}
					onClick={handleAddAllToCart}
					disabled={isLoadingCustomerParts || (customerParts?.length ?? 0) === 0}
				>
					Add all to cart
				</Button>
			</section>

			<AddedToCartModalEcomm
				isOpen={!!addedPart}
				partId={addedPart?.id}
				onClose={() => setAddedPart(null)}
			/>

			<AddedToCartModalEcomm
				isOpen={addedPartIds.length > 0}
				partIds={addedPartIds}
				onClose={() => setAddedPartIds([])}
			/>
		</PageContentWrapper>
	)
}
